import { useAtsConversationApi } from '@/composables/useApi';
import { useConversationMessageStore } from '@/core/conversations/message/conversation-message.store';
import {
  type ConversationMessage,
  type CreatedMessage,
  MessageMetadataType,
} from '@/core/conversations/message/conversation-message.type';
import MeService from '@/core/shared/me/me.service';
import DateUtils from '@/utils/date-utils';
import { useConversationIndexStore } from '@/core/conversations/conversation-index/conversation-index.store';
import ProProfileService from '@/core/conversations/pro-profile/pro-profile.service';
import { ConversationMessagePersistence } from '@/core/conversations/message/conversation-message.persistence';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import ApplicationScoreService from '@/core/conversations/application-score/application-score.service';
import ProjectService from '@/core/shared/project/project.service';
import type { UserMessageStatus } from './types/get-user-message-status.type';
import { type Employer } from '@factoryfixinc/ats-interfaces';
import type { ConversationIndex } from '../conversation-index/conversation-index.type';
import TrackingService from '@/core/shared/tracking/tracking.service';
import { TrackingActionName } from '@/core/shared/tracking/tracking-actions';
import TemplateService from '@/core/conversations/template/template.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import { useInterviewSchedulingStore } from '@/core/shared/interview-scheduling/interview-scheduling.store';
import InterviewSchedulingService from '@/core/shared/interview-scheduling/interview-scheduling.service';
import { InterviewType } from '@/core/shared/interview-scheduling/types/interview-scheduling.type';
import { USER_OPTED_OUT_ERROR_MESSAGE } from '@/core/sourcing/messaging/shared/constants/message-constants';
import { DoNotContactPersistence } from '../do-not-contact/do-not-contact.persistence';
import { mapDoNotContactEvents } from '../do-not-contact/map-do-not-contact-events';
import { cancelMessageFetches, isRepeatOfPageInFlight, trackMessageFetch } from './message-fetches';

export default class ConversationMessageService {
  private readonly messageStore = useConversationMessageStore();
  private readonly conversationIndexStore = useConversationIndexStore();
  private readonly conversationMessagePersistence = new ConversationMessagePersistence();
  private readonly interviewSchedulingStore = useInterviewSchedulingStore();
  private readonly interviewSchedulingService = new InterviewSchedulingService();
  private readonly meService = new MeService();
  private readonly doNotContactPersistence = new DoNotContactPersistence();

  public get doNotContactHistoryUnavailable(): boolean {
    return this.messageStore.doNotContactHistoryUnavailable;
  }

  public get scrollMessagesToBottom(): boolean {
    return this.conversationIndexStore.scrollMessagesToBottom;
  }

  public set scrollMessagesToBottom(value: boolean) {
    this.conversationIndexStore.scrollMessagesToBottom = value;
  }

  public get sessionUnsentMessages(): { [key: number]: string } {
    return this.messageStore.sessionUnsentMessages;
  }

  public set sessionUnsentMessages(value: { [key: number]: string }) {
    this.messageStore.sessionUnsentMessages = value;
  }

  public get messagesPollingIntervalId(): number | undefined {
    return this.messageStore.messagesPollingIntervalId;
  }

  public set messagesPollingIntervalId(value: number | undefined) {
    this.messageStore.messagesPollingIntervalId = value;
  }

  /**
   * Opens the message generation for a newly selected conversation. Selection is
   * the real boundary: a conversation whose pane never fetched messages would
   * otherwise leave both of its neighbours' loads on one generation.
   */
  public beginConversationLoad(conversationId: number): void {
    this.startConversationLoad(conversationId);
  }

  private startConversationLoad(conversationId: number): number {
    const requestId = this.messageStore.beginConversationLoad(conversationId);
    cancelMessageFetches(this.messageStore, (fetch) => fetch.requestId !== requestId);
    return requestId;
  }

  public async fetchConversationMessages(
    conversationId: number,
    opts: { page: number; clearMessages?: boolean } = { page: 1 },
  ): Promise<ConversationMessage[]> {
    const employerId = this.meService.getSelectedEmployerId();
    const selected = this.conversationIndexStore.selectedConversationIndex;
    const selectedConversationId = selected?.conversationId;
    const userProfileId = selected?.proId;
    // A queued poll can start after its conversation has already been deselected.
    if (selectedConversationId !== conversationId) return [];
    // Identity alone cannot reject the first response in an A -> B -> A switch.
    const requestId = this.startConversationLoad(conversationId);
    if (isRepeatOfPageInFlight(this.messageStore, requestId, opts)) return [];
    // Ownership lasts through enrichment and the commit, not just the HTTP request.
    return trackMessageFetch(this.messageStore, requestId, opts.page, async (signal) => {
      const isCurrentAttempt = () =>
        !signal.aborted &&
        this.messageStore.messageListRequestId === requestId &&
        this.meService.getSelectedEmployerId() === employerId &&
        this.conversationIndexStore.selectedConversationIndex?.conversationId ===
          selectedConversationId &&
        this.conversationIndexStore.selectedConversationIndex?.proId === userProfileId;

      if (opts.clearMessages) this.clearMessageList();

      const messages = await this.conversationMessagePersistence.fetchConversationMessages(
        conversationId,
        opts.page,
        { signal },
      );
      if (signal.aborted) return [];

      const mappedMessages: ConversationMessage[] =
        messages?.map((message) => ({
          id: message.id,
          key: `message:${message.id}`,
          content: message.content,
          conversationId: message.conversationId,
          createdAt: DateUtils.dateStringToDate(message.createTs),
          sender: {
            id: message.sender?.senderId ?? null,
            name: message?.sender?.name ?? '',
          },
        })) ?? [];

      const scoreBlurbMessages = await this.mapApplicationScoreToMessages(
        conversationId,
        mappedMessages,
      );
      const applicationStatusMessages = await this.mapApplicantStatusHistory(conversationId);
      const interviewSchedulingMessages = await this.mapInterviewSchedulingEvents(conversationId);
      let doNotContactMessages: ConversationMessage[] = [];
      let historyUnavailable: boolean | undefined;
      if (
        opts.page === 1 &&
        employerId &&
        userProfileId &&
        selectedConversationId === conversationId
      ) {
        try {
          const history = await this.doNotContactPersistence.getHistory(employerId, userProfileId);
          doNotContactMessages = mapDoNotContactEvents(conversationId, history);
          historyUnavailable = false;
        } catch (error) {
          if (!isCurrentAttempt()) return [];
          historyUnavailable = true;
          // Persistence produces a sanitized error, never the history response or reason.
          if (!this.doNotContactHistoryUnavailable) ErrorService.captureException(error);
        }
      }

      // The message request may have completed before the user switched during the history read.
      if (!isCurrentAttempt()) return [];
      if (historyUnavailable !== undefined) {
        this.messageStore.doNotContactHistoryUnavailable = historyUnavailable;
      }
      const allMessages = [
        ...mappedMessages,
        ...scoreBlurbMessages,
        ...applicationStatusMessages,
        ...interviewSchedulingMessages,
        ...doNotContactMessages,
      ];

      if (opts.clearMessages) {
        this.messageStore.setMessages(allMessages);
      } else {
        this.messageStore.pushMessages(allMessages);
      }

      const lastMessageId = this.messageStore.textOnlyMessages.at(-1)?.id;

      // limit mark read to first page only
      if (opts.page === 1 && lastMessageId) this.markAsRead(lastMessageId);

      return mappedMessages || [];
    });
  }

  private async markAsRead(messageId: ConversationMessage['id']) {
    const isMessageAlreadyMarkedAsRead = messageId === this.messageStore.lastMessageMarkedAsRead;
    if (isMessageAlreadyMarkedAsRead) {
      return;
    }

    const url = `/message/${messageId}/change-read-status`;
    const { error } = await useAtsConversationApi(url).patch();

    if (error.value) {
      throw new Error(error.value);
    }

    this.messageStore.lastMessageMarkedAsRead = messageId;
    this.updateSelectedHeaderReadStatus();
  }

  public clearMessageList(): void {
    this.messageStore.clearMessageList();
    this.messageStore.lastMessageMarkedAsRead = undefined;
  }

  public async sendMessage(
    conversationId: number,
    message: string,
    jobId?: number,
  ): Promise<CreatedMessage | undefined> {
    try {
      const createdMessage = await this.conversationMessagePersistence.sendMessage(
        conversationId,
        message,
        { jobId },
      );

      if (!createdMessage) {
        throw new Error('Could not send a message');
      }

      const senderName = `${this.meService.userProfile?.nameFirst} ${this.meService.userProfile?.nameLast}`;
      this.messageStore.pushMessages([
        {
          content: createdMessage.content,
          conversationId,
          createdAt: createdMessage.createTs,
          sender: {
            id: createdMessage.senderId,
            name: senderName,
          },
          id: createdMessage.id,
          key: `message:${createdMessage.id}`,
        },
      ]);
      this.updateConversationStateAfterSend(createdMessage.conversationId, createdMessage.content);
      return createdMessage;
    } catch (error) {
      if (error instanceof Error && error.message === USER_OPTED_OUT_ERROR_MESSAGE) {
        SnackbarService.showSnackbar({
          text: '<span class="font-bold">Message not sent:</span> Candidate opted out of messaging',
          appendIcon: 'close',
          horizontalPosition: 'center',
          prependIcon: 'inform',
        });
      }
      throw error;
    }
  }

  private updateConversationStateAfterSend(conversationId: number, messageSent: string) {
    const updateConversationState = (conversationIndex: ConversationIndex): ConversationIndex => {
      return {
        ...conversationIndex,
        isWatching: true,
        readStatus: true,
        lastMessageText: messageSent,
      };
    };

    const selectedConversationIndex = this.conversationIndexStore.selectedConversationIndex;
    if (selectedConversationIndex?.conversationId === conversationId) {
      this.conversationIndexStore.selectedConversationIndex =
        updateConversationState(selectedConversationIndex);
    }

    this.conversationIndexStore.conversationIndexes = (
      this.conversationIndexStore.conversationIndexes ?? []
    ).map((conversationIndex) =>
      conversationIndex.conversationId === conversationId
        ? updateConversationState(conversationIndex)
        : conversationIndex,
    );
  }

  private updateSelectedHeaderReadStatus(messageSent?: string) {
    const { selectedConversationIndex: selectedHeader } = this.conversationIndexStore;
    if (selectedHeader) {
      selectedHeader.readStatus = true;
      selectedHeader.lastMessageText = messageSent || selectedHeader.lastMessageText;
    }
  }

  public get messageList(): ConversationMessage[] {
    return this.messageStore.allMessages;
  }

  public async mapApplicationScoreToMessages(
    conversationId: number,
    messages: ConversationMessage[],
  ): Promise<ConversationMessage[]> {
    const proProfileService = new ProProfileService();
    const projectService = new ProjectService();
    const applicationScoreService = new ApplicationScoreService();
    const applications = proProfileService.selectedProProfile?.jobApplicants ?? [];

    const filteredApplications = applications
      // Filter applications that have a score, an applicant approval comment and
      // were scored after the new scoring release date.
      .filter((application) => {
        const newScoringRelease = new Date('2024-01-30').setHours(0, 0, 0, 0); // ONE-5
        const hasScore = typeof application.score === 'number';
        const applicationTs = application.submitTs ?? application.applyTs ?? application.createTs;
        const hasBlurb = !!application.applicantApprovalComment;

        if (!hasScore || !hasBlurb || !applicationTs) {
          return false;
        }

        const scoredTs = new Date(applicationTs).valueOf();

        return scoredTs >= newScoringRelease;
      });

    const scoreBlurbs: ConversationMessage[] = [];
    const applicationsScorePromises = await Promise.allSettled(
      filteredApplications.map((application) => {
        return applicationScoreService.getScoreDetail(application.id);
      }),
    );
    const applicationsScore = applicationsScorePromises.map((promise) => {
      return promise.status === 'fulfilled' ? promise.value : undefined;
    });

    // Map filtered applications to match the conversation message interface
    for (const application of filteredApplications) {
      const applicationTs = application.submitTs ?? application.applyTs ?? application.createTs;
      const scoredTs = applicationTs as string;
      const createdAt = DateUtils.dateStringToDate(scoredTs);
      const jobTitle = projectService.projectTitlesByJobId.get(application.jobId) ?? '';
      const scoreDetail = applicationsScore.find(
        (score) => score?.jobApplicationId === application.id,
      );
      const score = ApplicationScoreService.formatScore(scoreDetail?.score);
      const chips = ApplicationScoreService.getScoreChipsInfo(scoreDetail);
      const scoreVersion = scoreDetail?.version;

      scoreBlurbs.push({
        id: application.id,
        key: `score:${application.id}`,
        conversationId,
        content: scoreDetail?.summary ?? '',
        createdAt,
        metadata: {
          type: MessageMetadataType.SCORE_BLURB,
          score,
          chips,
          scoreVersion,
          jobTitle,
          applicationId: application.id,
        },
      });
    }

    // Ensure only scores that are newer than the oldest message are displayed (with a 5-minute buffer for Copilot Outreach scoring)
    const minCreatedAt = messages.reduce(
      (min, message) => (message.createdAt < min ? message.createdAt : min),
      new Date(),
    );
    const bufferTime = 5 * 60 * 1000; // 5 minutes
    const minCreatedAtWithBuffer = new Date(minCreatedAt.getTime() - bufferTime);

    return scoreBlurbs.filter((scoreBlurb) => {
      return scoreBlurb.createdAt >= minCreatedAtWithBuffer;
    });
  }

  public async mapApplicantStatusHistory(conversationId: number): Promise<ConversationMessage[]> {
    const proProfileService = new ProProfileService();

    return proProfileService.selectedProJobApplicantStatusHistory.map((status) => {
      const createdAt = DateUtils.dateStringToDate(status.createTs);
      return {
        id: status.id,
        key: `status:${status.id}`,
        conversationId,
        content: status.status,
        createdAt,
        sender: {
          id: status.creator?.id,
          name: [status.creator?.nameFirst, status.creator?.nameLast].filter(Boolean).join(' '),
        },
        metadata: {
          type: MessageMetadataType.APPLICATION_STATUS,
        },
      };
    });
  }

  public async mapInterviewSchedulingEvents(
    conversationId: number,
  ): Promise<ConversationMessage[]> {
    const DEFAULT_AI_RECRUITER_NAME = 'AI Recruiter';

    try {
      // Await the fetch — deduplicates if conversations.vue already triggered it
      const employerId = this.meService.employer?.id;
      if (employerId) {
        await this.interviewSchedulingService.fetchScheduledEvents(employerId, conversationId);
      }

      // Guard: if the store has events for a different conversation, skip
      if (this.interviewSchedulingStore.scheduledEventsConversationId !== conversationId) {
        return [];
      }

      const events = this.interviewSchedulingStore.scheduledEvents;

      // We need to remove old events that don't have all the required fields to display the message.
      const filteredEvents = events.filter((event) => {
        const requiredFields = [
          event.initiationSource,
          event.recruiterName,
          event.aiRecruiterName,
          event.type,
          event.jobTitle,
        ];
        return requiredFields.every((field) => field !== null && field !== undefined);
      });

      return filteredEvents.map((event) => {
        const isScheduled = !!event.scheduleDateTs;
        const aiRecruiterName = event.aiRecruiterName || DEFAULT_AI_RECRUITER_NAME;
        const interviewTypeDisplay =
          event.type === InterviewType.on_site ? 'in-person' : event.type;

        const createdAt = DateUtils.dateStringToDate(event.updateTs);

        return {
          id: event.id,
          key: `interview:${event.id}`,
          conversationId,
          content: '', // Content is handled by the MessageInterviewScheduling component instead of using the v-html directive
          createdAt,
          sender: {
            id: event.recruiterUserProfileId,
            name: aiRecruiterName,
          },
          metadata: {
            type: isScheduled
              ? MessageMetadataType.INTERVIEW_SCHEDULED
              : MessageMetadataType.INTERVIEW_SCHEDULING_INITIATED,
            threadId: event.threadId,
            initiationSource: event.initiationSource,
            recruiterName: event.recruiterName,
            aiRecruiterName,
            scheduleDateTs: event.scheduleDateTs ?? undefined,
            interviewType: event.type,
            interviewTypeDisplay: interviewTypeDisplay,
            jobTitle: event.jobTitle,
          },
        };
      });
    } catch (error) {
      ErrorService.captureException(error);
      return [];
    }
  }

  public async getUserMessageStatus(
    userProfileId: number,
    employerId: Employer['id'],
  ): Promise<UserMessageStatus> {
    return this.conversationMessagePersistence.getUserMessageStatus(userProfileId, employerId);
  }

  // sessionUnsentMessages
  public getSessionUnsentMessageByApplicantId(applicantId: number): string {
    const storedMessage = this.sessionUnsentMessages[applicantId] ?? '';
    return storedMessage;
  }

  public setSessionUnsentMessageForApplicantId(applicantId: number, message: string): void {
    this.sessionUnsentMessages[applicantId] = message;
  }

  public clearSessionUnsentMessageForApplicantId(applicantId: number): void {
    delete this.sessionUnsentMessages[applicantId];
  }

  public async sendBulkMessagesToConversation(
    conversationIndexes: Array<
      Pick<
        ConversationIndex,
        'conversationId' | 'proId' | 'proName' | 'proEmail' | 'primaryApplicationIndex'
      >
    >,
    message: string,
  ): Promise<void> {
    if (conversationIndexes.length === 0 || !message) return;

    const templateService = new TemplateService();
    const promises = conversationIndexes.map((conversationIndex) => {
      // Replace dynamic variables with actual values for each conversation
      const personalizedMessage = templateService.mapTemplatePlaceholders(
        message,
        false,
        conversationIndex.proName,
        conversationIndex.primaryApplicationIndex?.jobDisplayTitle,
      );
      return this.conversationMessagePersistence.sendMessage(
        conversationIndex.conversationId,
        personalizedMessage,
        {
          jobId: conversationIndex.primaryApplicationIndex?.jobId,
        },
      );
    });
    const results = await Promise.allSettled(promises);

    // Get the fulfilled results
    const fulfilledResults = results.filter(
      (result) => result.status === 'fulfilled',
    ) as PromiseFulfilledResult<CreatedMessage>[];

    // Get the rejected results
    const rejectedResults = results.filter(
      (result) => result.status === 'rejected',
    ) as PromiseRejectedResult[];

    if (rejectedResults.length === promises.length) {
      throw new Error('All messages were rejected');
    }

    this.updateMessageStoreWithBulkMessageResults(fulfilledResults);

    this.showSnackbarForBulkMessageResults(fulfilledResults, rejectedResults);

    // Track the action
    TrackingService.trackAction(TrackingActionName.MESSAGE_SENT, {
      num_pros: conversationIndexes.length,
      conversation_id: conversationIndexes.map((index) => index.conversationId),
      message_content: message,
    });
  }

  /**
   * Updates the message store with the results of the bulk messages
   * @param fulfilledResults - The fulfilled results of the bulk messages
   */
  private updateMessageStoreWithBulkMessageResults(
    fulfilledResults: PromiseFulfilledResult<CreatedMessage>[],
  ) {
    const senderName = `${this.meService.userProfile?.nameFirst} ${this.meService.userProfile?.nameLast}`;

    // We only need to get the first message to get the conversationId since
    // all the messages are from the same conversation
    const messageStore = this.messageStore.textOnlyMessages[0];
    if (!messageStore) return;

    // We need to check which fullfiledResult should be used to update the message store with
    const settledMessage = fulfilledResults.find(
      (result) => result.value?.conversationId === messageStore.conversationId,
    );
    const createdMessage = settledMessage?.value;
    if (createdMessage) {
      this.messageStore.pushMessages([
        {
          content: createdMessage.content,
          conversationId: createdMessage.conversationId,
          createdAt: createdMessage.createTs,
          sender: {
            id: createdMessage.senderId,
            name: senderName,
          },
          id: createdMessage.id,
          key: `message:${createdMessage.id}`,
        },
      ]);
    }

    fulfilledResults.forEach((result) => {
      this.updateConversationStateAfterSend(result.value.conversationId, result.value.content);
    });
  }

  /**
   * Shows a snackbar for the results of the bulk messages
   * @param fulfilledResults - The fulfilled results of the bulk messages
   * @param rejectedResults - The rejected results of the bulk messages
   */
  private showSnackbarForBulkMessageResults(
    fulfilledResults: PromiseSettledResult<CreatedMessage>[],
    rejectedResults: PromiseRejectedResult[],
  ) {
    const textParts: string[] = [];

    if (fulfilledResults.length) {
      const count = fulfilledResults.length;
      const prefix =
        count > 1
          ? `Messages were sent to ${count} candidates`
          : 'The message was sent to 1 candidate';
      textParts.push(prefix);
    }

    if (rejectedResults.length) {
      const count = rejectedResults.length;
      const text = count > 1 ? `${count} messages failed to send` : '1 message failed to send';
      textParts.push(text);
    }

    SnackbarService.showSnackbar({
      variant: rejectedResults.length ? 'caution-alt' : 'success',
      text: textParts.join(', '),
      appendIcon: 'close',
      horizontalPosition: 'left',
    });
  }
}
